/**
 * Vídeo com autoplay e fallback para iOS (economia de bateria).
 * Exibe preview por cima até o vídeo tocar; em falha de autoplay,
 * aguarda a primeira interação do usuário para dar play.
 */
class ZrzsAutoplayVideo extends HTMLElement {
  constructor() {
    super();
    /** @type {boolean} */
    this._initialized = false;
    /** @type {boolean} */
    this._resumeListenersBound = false;
    /** @type {boolean} */
    this._wantPreload = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const video = this.querySelector('video');
    const overlayNode = this.querySelector('[data-zrzs-autoplay-video-overlay]');
    if (!(video instanceof HTMLVideoElement)) return;

    const overlay =
      overlayNode instanceof HTMLElement ? overlayNode : null;

    /** @type {HTMLVideoElement} */
    this._video = video;
    /** @type {HTMLElement | null} */
    this._overlay = overlay;

    // Overlay só cobre o vídeo até o primeiro frame real. Reexibir no
    // `pause` (loop nativo, pause programático no scroll, seek) faz o
    // poster piscar por cima do frame congelado.
    const hideOverlay = () => {
      if (overlay) overlay.classList.add('hidden');
      if (video.hasAttribute('poster')) video.removeAttribute('poster');
    };
    video.addEventListener('playing', hideOverlay);
    if (!video.paused && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      hideOverlay();
    }

    if (this.offsetWidth === 0 && this.offsetHeight === 0) {
      // Escondido (ex.: conteúdo de persona do Miragem Scroll, que fica
      // todo pré-renderizado e escondido até ser ativado) — não tenta
      // carregar nem dar play agora, só desliga o preload nativo. Vários
      // vídeos com autoplay escondidos ao mesmo tempo competem por
      // rede/CPU sem nenhum benefício. Quem ativa o conteúdo depois chama
      // `retryPlay()` — ou `preloadMedia()` se só quiser bufferizar.
      // `preloadMedia` pode ter rodado antes do upgrade; nesse caso não
      // desliga o preload que ela pediu.
      if (!this._wantPreload) video.preload = 'none';
      return;
    }

    this._tryPlay(video, overlay);
  }

  /**
   * Tenta dar play num vídeo que nasceu escondido (ver `connectedCallback`)
   * — chamar assim que ele passar a estar visível de verdade.
   */
  retryPlay() {
    if (!(this._video instanceof HTMLVideoElement)) return;
    if (this._video.preload === 'none') this._video.preload = 'metadata';
    this._tryPlay(this._video, this._overlay);
  }

  /**
   * Começa a baixar o arquivo sem dar play — pra aquecer um vídeo que
   * ainda está escondido (conteúdo de persona do Miragem, materializado
   * ao chegar no slide, mas o painel só abre no "ver modelos").
   */
  preloadMedia() {
    const video =
      this._video instanceof HTMLVideoElement
        ? this._video
        : this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;
    this._wantPreload = true;
    video.preload = 'auto';
    try {
      video.load();
    } catch {
      /* ignore */
    }
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {HTMLElement | null} overlay
   */
  _tryPlay(video, overlay) {
    video.play().catch(() => {
      if (overlay instanceof HTMLElement) {
        overlay.classList.remove('hidden');
      }
      this._bindResumeOnUserInteraction(video);
    });
  }

  /**
   * @param {HTMLVideoElement} video
   */
  _bindResumeOnUserInteraction(video) {
    if (this._resumeListenersBound) return;
    this._resumeListenersBound = true;

    const resume = () => {
      video.play().catch(() => {});
    };

    document.addEventListener('click', resume, { once: true });
    document.addEventListener('touchstart', resume, { once: true });
    window.addEventListener('scroll', resume, { once: true, passive: true });
  }
}

if (!customElements.get('zrzs-autoplay-video')) {
  customElements.define('zrzs-autoplay-video', ZrzsAutoplayVideo);
}
